import { ArrowSquareOut, Plus, Warning } from 'phosphor-react'
import { useCallback, useEffect, useState } from 'react'
import { Badge, Button, Col, Modal, Row, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import DashboardLayout from '../../../../layouts/Dashboard'
import api from '../../../../services/api'
import { formatDateTSX } from '../../../../utils/tsxFormat'
import { formatMoney } from '../../../../utils/format'

import styles from './styles.module.css'
import VehicleCard from '../../../../components/VehicleCard'
import { useLoading } from '../../../../hooks/LoadingContext'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import SearchInput from '../../../../components/SearchInput'

interface IMaintance {
  id: number
  title: string
  vehicle_board: string
  vehicle_model: string
  vehicle_color: string
  driver_name: string
  description: string
  invoice_url: string
  date: Date
  cost: number
  mileage: number
}

interface IMaintanceCard {
  Título: string
  Motorista: string
  Custo: number
  Data: string
}

const Manutencoes: React.FC = () => {
  const [search, setSearch] = useState<string>('')
  const [lastSearch, setLastSearch] = useState<string>('')
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)
  const { isLoading, setIsLoading } = useLoading()
  const [maintances, setMaintances] = useState<IMaintance[]>()

  const [showMaintance, setShowMaintance] = useState<boolean>(false)
  const [maintance, setMaintance] = useState<IMaintance>()

  const loadData = useCallback(async () => {
    const isSearchDifferent = search !== lastSearch
    if (!hasMoreData && !isSearchDifferent) return

    setIsLoading(true)

    if (isSearchDifferent) {
      setLastSearch(search)
      setHasMoreData(true)
    }

    let apiUrl = '/delivery-manager/vehicle/maintances?limit=20'
    if (search) apiUrl += '&search=' + search

    const offset = maintances && !isSearchDifferent ? maintances.length : 0
    if (offset) apiUrl += '&offset=' + offset

    try {
      const { data } = await api.get(apiUrl)
      if (!data.length) {
        if (isSearchDifferent) setMaintances(data)
        setHasMoreData(false)
      } else {
        if (!maintances) setMaintances(data)
        else setMaintances(isSearchDifferent ? data : maintances.concat(data))
      }
      setIsLoading(false)
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, maintances, hasMoreData, setIsLoading])

  useEffect(() => {
    if (!maintances && hasMoreData) loadData()
  }, [loadData, hasMoreData, maintances])

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop !==
          document.documentElement.offsetHeight ||
        isLoading
      ) {
        return
      }
      loadData()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoading, loadData])

  const navigate = useNavigate()

  const getCardData = (data: IMaintance) => {
    let newData: IMaintanceCard = {
      Título: data.title,
      Motorista: data.driver_name,
      Custo: data.cost,
      Data: formatDateTSX(data.date, "dd/MM/yyyy 'às' HH:mm:ss"),
    }

    return newData
  }

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Manutenções">
      <LoadingSpinner text="Buscando manutenções. Aguarde..." />
      <Row style={{ marginInline: 30 }}>
        <SearchInput
          placeholder="Digite a placa do veículo ou o nome do motorista"
          searchValue={search}
          setValue={setSearch}
          handleSearch={loadData}
          showOptions={false}
        />
        <Col
          style={{ display: 'flex', marginInline: 30 }}
          className="justify-content-md-end justify-content-center"
        >
          <Button
            className={`${styles.newBtn} px-3 px-md-5`}
            onClick={() => navigate('/gestor-entregas/veiculos/manutencoes/nova')}
          >
            <Plus
              style={{ marginBottom: 4, marginRight: 4 }}
              color="#fff"
              size={15}
              weight="bold"
            />
            Nova manutenção
          </Button>
        </Col>
      </Row>
      <Row
        style={{ marginInline: 30, display: 'flex' }}
        className="justify-content-center justify-content-md-start"
      >
        {isLoading && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 40,
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: '#053C5E',
                marginRight: 10,
              }}
            />
          </div>
        )}
        {!maintances?.length && !isLoading && (
          <Badge
            bg=""
            text="white"
            style={{
              textAlign: 'center',
              marginTop: 40,
              backgroundColor: '#053c5e',
            }}
          >
            <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" /> Nenhuma
            manutenção encontrada!
          </Badge>
        )}
        {maintances?.map((maintance, index) => (
          <VehicleCard
            key={index}
            data={getCardData(maintance)}
            id={maintance.id}
            title={maintance.vehicle_board}
            subtitle={`${maintance.vehicle_model} ${maintance.vehicle_color}`}
            seeMore={() => [setShowMaintance(true), setMaintance(maintance)]}
            urlEdit={'/gestor-entregas/veiculos/manutencoes/editar'}
          />
        ))}
      </Row>
      {showMaintance && (
        <Modal centered size="lg" show={showMaintance} onHide={() => setShowMaintance(false)}>
          <Modal.Header closeButton />
          <Modal.Body style={{ marginLeft: 20, marginRight: 20 }}>
            <Row style={{ marginBottom: 15 }}>
              <span className={styles.infoSpan}>
                <strong>Título: </strong>
                {maintance?.title}
              </span>
              <span className={styles.infoSpan}>
                <strong>Descrição: </strong>
                {maintance?.description}
              </span>
              <span className={styles.infoSpan}>
                <strong>Veículo: </strong>
                {`${maintance?.vehicle_model} ${maintance?.vehicle_color}`}
              </span>
              <span className={styles.infoSpan}>
                <strong>Placa: </strong>
                {maintance?.vehicle_board}
              </span>
              <span className={styles.infoSpan}>
                <strong>Motorista: </strong>{' '}
                {maintance?.driver_name ? maintance?.driver_name : 'Não informado'}
              </span>
              <span className={styles.infoSpan}>
                <strong>Quilometragem: </strong>
                {`${maintance?.mileage} km`}
              </span>
              <span className={styles.infoSpan}>
                <strong>Custo: </strong> {formatMoney(maintance?.cost)}
              </span>
              <span className={styles.infoSpan}>
                <strong>Data:</strong> {formatDateTSX(maintance?.date, "dd/MM/yyyy 'às' HH:mm:ss")}
              </span>
            </Row>
            <Row>
              <Button
                className={styles.cardBtn}
                onClick={() => {
                  window.open(maintance?.invoice_url, '_blank')
                }}
              >
                Nota fiscal
                <ArrowSquareOut style={{ marginTop: -3, marginLeft: 3 }} size={18} />
              </Button>
            </Row>
          </Modal.Body>
          <Row className="text-center">
            <hr></hr>
            <Col>
              <Button className={styles.closeBtn} onClick={() => setShowMaintance(false)}>
                Fechar
              </Button>
            </Col>
          </Row>
        </Modal>
      )}
    </DashboardLayout>
  )
}

export default Manutencoes
